
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import language files
import enTranslation from './locales/en.json';
import deTranslation from './locales/de.json';
import arTranslation from './locales/ar.json';
import trTranslation from './locales/tr.json';
import kuTranslation from './locales/ku.json';

// Define resources
const resources = {
  en: {
    translation: enTranslation
  },
  de: {
    translation: deTranslation
  },
  ar: {
    translation: arTranslation
  },
  tr: {
    translation: trTranslation
  },
  ku: {
    translation: kuTranslation
  }
};

// Set the HTML dir attribute based on the language
const setHtmlDirection = (language: string) => {
  const rtlLanguages = ['ar', 'he', 'ur', 'fa'];
  document.documentElement.dir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
};

// Get user's browser language or saved preference
const getUserLanguage = () => {
  const savedLanguage = localStorage.getItem('i18nextLng');
  if (savedLanguage && Object.keys(resources).includes(savedLanguage)) {
    return savedLanguage;
  }
  
  // Check browser language
  const browserLang = navigator.language.split('-')[0];
  return Object.keys(resources).includes(browserLang) ? browserLang : 'en';
};

// Initialize i18next with all required configuration
i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    lng: getUserLanguage(), // Use detected language
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag', 'path', 'cookie'],
      caches: ['localStorage', 'cookie'],
      lookupLocalStorage: 'i18nextLng',
    },
    interpolation: {
      escapeValue: false, // React already escapes content
    },
    react: {
      useSuspense: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'span', 'a', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
      transSupportBasicHtmlNodes: true,
    },
    compatibilityJSON: 'v4',
    returnEmptyString: false, // Prevent empty strings as translations
    returnNull: false, // Prevent null as translations
  });

// Set direction on initial load and language change
setHtmlDirection(i18n.language);
i18n.on('languageChanged', (lng) => {
  setHtmlDirection(lng);
});

// Add debug event handlers
if (import.meta.env.DEV) {
  i18n.on('initialized', () => {
    console.info('i18next initialized:', i18n.language);
  });

  i18n.on('languageChanged', (lng) => {
    console.info('Language changed to:', lng);
  });

  i18n.on('missingKey', (lngs, namespace, key) => {
    console.warn(`Missing translation key: ${key} for languages: ${lngs.join(', ')} in namespace: ${namespace}`);
  });
}

export default i18n;
